import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useMediaQuery, Typography, Grid, colors } from '@material-ui/core';
import Pagination from '@material-ui/lab/Pagination';
import {
  useQueryParams,
  NumberParam,
  BooleanParam,
  withDefault,
} from 'use-query-params';
import { useTranslation } from 'react-i18next';

import { LegislationService } from 'services';
import { SimpleSelect } from 'components/molecules';
import { Section } from 'components/organisms';
import { Hero, CardDocument } from './components';
import { LegislationContext } from './contexts';

const legislationService = new LegislationService();

const PAGE_SIZE = 5;

function paginate(array, pageSize, pageNumber) {
  return array.slice((pageNumber - 1) * pageSize, pageNumber * pageSize);
}

function getPageNumber(array, id, pageSize = PAGE_SIZE) {
  const elementIndex = array.findIndex(item => item.id === id);

  if (elementIndex < 0) return 1;

  return Math.floor(elementIndex / pageSize) + 1;
}

const useStyles = makeStyles(theme => ({
  root: {},
  subtitle: {
    fontFamily: 'Gilroy',
    fontWeight: '600',
    marginBottom: theme.spacing(2),
  },
  section: {
    paddingTop: theme.spacing(7),
    paddingLeft: theme.spacing(14),
    paddingRight: theme.spacing(14),
    [theme.breakpoints.down('md')]: {
      paddingRight: theme.spacing(8),
      paddingLeft: theme.spacing(8),
    },
    [theme.breakpoints.down('xs')]: {
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(3),
    },
  },
  simpleSelectContainer: {
    display: 'flex',
    flexDirection: 'row-reverse',
    marginBottom: 20,
  },
  actsContainer: {},
  regulationsContainer: {},
  pagination: {
    marginTop: theme.spacing(2),
    display: 'flex',
    justifyContent: 'center',
  },
  divider: {
    borderLeft: '1px solid #A4A4A4',
    borderTop: 0,
    [theme.breakpoints.down('sm')]: {
      borderLeft: 0,
      borderTop: '1px solid #A4A4A4',
    },
  },
  regulationCard: {
    '& .MuiCardActionArea-root': {
      paddingRight: theme.spacing(2),
    },
    [theme.breakpoints.up('md')]: {
      '& .MuiCardActionArea-root': {
        paddingRight: theme.spacing(2),
      },
    },
  },
}));

const Legislation = props => {
  const { className, themeMode, staticContext, ...rest } = props;
  const classes = useStyles();
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });
  const { t } = useTranslation();
  const [regulations, setRegulations] = useState([]);
  const [acts, setActs] = useState([]);
  const [actYears, setActYears] = useState([]);
  const [regYears, setRegYears] = useState([]);
  const [filters, setFilters] = useQueryParams({
    debug: BooleanParam,
    actYear: NumberParam,
    regYear: NumberParam,
    actPage: withDefault(NumberParam, 1),
    regPage: withDefault(NumberParam, 1),
    category: withDefault(NumberParam, 2),
    act: NumberParam,
    regulation: NumberParam,
  });

  // load all regulation when at least a regulation is selected
  useEffect(() => {
    if (!filters.act && filters.regulation) return;

    legislationService
      .regulations(filters.category, filters.regYear, filters.act)
      .then(({ data }) => {
        setRegulations(data);
      });
  }, [filters, filters.act, filters.category, filters.regYear]);

  useEffect(() => {
    legislationService
      .regulations(filters.category, filters.regYear, filters.act)
      .then(({ data }) => {
        if (filters.regulation) {
          const currentPage = getPageNumber(data, filters.regulation);
          setFilters(prev => ({ ...prev, regPage: currentPage }));
        }

        setRegulations(data);
      });

    legislationService
      .acts(filters.category, filters.actYear, filters.regulation)
      .then(({ data }) => {
        if (filters.act) {
          const currentPage = getPageNumber(data, filters.act);
          setFilters(prev => ({ ...prev, actPage: currentPage }));
        }
        setActs(data);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (filters.act && !filters.regulation) return;

    legislationService
      .acts(filters.category, filters.actYear, filters.regulation)
      .then(({ data }) => {
        setActs(data);
      });
  }, [
    filters.category,
    filters.actYear,
    filters.regulation,
    setFilters,
    filters.act,
  ]);

  useEffect(() => {
    legislationService.actYears(filters.category).then(({ data }) => {
      setActYears(data);
    });
    legislationService.regulationYears(filters.category).then(({ data }) => {
      setRegYears(data);
    });
  }, [filters.category]);

  const handleActYearChange = event => {
    setFilters(prev => ({
      ...prev,
      regYear: undefined,
      regulation: undefined,
      actYear: event.target.value,
      act: undefined,
      regPage: 1,
      actPage: 1,
    }));
  };

  const handleRegulationYearChange = event => {
    setFilters(prev => ({
      ...prev,
      actYear: undefined,
      act: undefined,
      regulation: undefined,
      regYear: event.target.value,
      regPage: 1,
      actPage: 1,
    }));
  };

  const handleClickAct = (event, act) => {
    setFilters(prev => ({
      ...prev,
      regYear: undefined,
      regulation: undefined,
      act: act.id,
      regPage: 1,
    }));
  };

  const handleClickRegulation = (event, regulation) => {
    setFilters(prev => ({
      ...prev,
      regulation: regulation.id,
      act: undefined,
      actYear: undefined,
      actPage: 1,
    }));
  };

  const handleActPageChange = (event, value) => {
    setFilters(prev => ({ ...prev, actPage: value }));
  };

  const handleRegulationPageChange = (event, value) => {
    setFilters(prev => ({ ...prev, regPage: value }));
  };

  // const actInfo = useMemo(() => {
  //   if (!filters.debug) return;

  //   const { actYear, category, regulation } = filters;

  //   if (regulation) return `You have filtered by regulation ${regulation}`;
  //   if (actYear) return `You have filtered by year ${actYear}`;
  //   if (category) return `You have filtered by category ${category}`;
  // }, [filters]);

  // const regInfo = useMemo(() => {
  //   if (!filters.debug) return;

  //   const { regYear, category, act } = filters;

  //   if (act) return `You have filtered by act ${act}`;
  //   if (regYear) return `You have filtered by year ${regYear}`;
  //   if (category) return `You have filtered by category ${category}`;
  // }, [filters]);

  return (
    <LegislationContext.Provider value={{ filters, setFilters }}>
      <div className={className} {...rest}>
        <Hero />
        <Section className={classes.section}>
          <Grid
            container
            spacing={isMd ? 8 : 4}
            className={classes.actsContainer}
          >
            <Grid item md={6} xs={12}>
              <Grid item xs={12}>
                <Typography variant="h4" className={classes.subtitle}>
                  {t('LEGISLATION.LEGAL_ACTS')}
                </Typography>
                <div className={classes.simpleSelectContainer}>
                  <SimpleSelect
                    name="year"
                    placeholder={t('LEGISLATION.SELECT_YEAR')}
                    startText={t('LEGISLATION.SELECTED_YEAR')}
                    items={actYears}
                    defaultValue=""
                    value={filters.actYear || ''}
                    onChange={handleActYearChange}
                    secondStyle={true}
                  />
                </div>
              </Grid>
              {paginate(acts, PAGE_SIZE, filters.actPage).map((item, index) => (
                <Grid item xs={12} key={index}>
                  <CardDocument
                    style={{ marginBottom: 20 }}
                    color={colors.red}
                    fontIconClass="fas fa-file-pdf"
                    imageUrl="assets/images/doc-orange.svg"
                    title={item.title}
                    subtitle={item.description}
                    href={item.url}
                    onClick={e => handleClickAct(e, item)}
                    selected={item.id === filters.act}
                    downloadText={t('COMMON.DOWNLOAD')}
                  />
                </Grid>
              ))}
              <div className={clsx(classes.pagination)}>
                <Pagination
                  count={Math.ceil(acts.length / PAGE_SIZE)}
                  page={filters.actPage}
                  onChange={handleActPageChange}
                  size="medium"
                />
              </div>
            </Grid>
            <Grid
              item
              className={clsx(classes.divider, classes.regulationsContainer)}
              md={6}
              xs={12}
            >
              <Grid item xs={12}>
                <Typography variant="h4" className={clsx(classes.subtitle)}>
                  {t('LEGISLATION.SUB-LEGAL_ACTS')}
                </Typography>
                <div className={classes.simpleSelectContainer}>
                  <SimpleSelect
                    name="regYear"
                    placeholder={t('LEGISLATION.SELECT_YEAR')}
                    startText={t('LEGISLATION.SELECTED_YEAR')}
                    items={regYears}
                    defaultValue=""
                    value={filters.regYear || ''}
                    onChange={handleRegulationYearChange}
                    secondStyle={true}
                  />
                </div>
              </Grid>
              {paginate(regulations, PAGE_SIZE, filters.regPage).map(
                (item, index) => (
                  <Grid item xs={12} key={index}>
                    <CardDocument
                      className={classes.regulationCard}
                      style={{ marginBottom: 20 }}
                      imageUrl="assets/images/doc-blue.svg"
                      color={colors.red}
                      fontIconClass="fas fa-file-pdf"
                      title={item.title}
                      subtitle={item.description}
                      href={item.url}
                      onClick={e => handleClickRegulation(e, item)}
                      selected={item.id === filters.regulation}
                      downloadText={t('COMMON.DOWNLOAD')}
                    />
                  </Grid>
                ),
              )}
              <div className={clsx(classes.pagination)}>
                <Pagination
                  count={Math.ceil(regulations.length / PAGE_SIZE)}
                  page={filters.regPage}
                  onChange={handleRegulationPageChange}
                  size="medium"
                />
              </div>
            </Grid>
          </Grid>
        </Section>
      </div>
    </LegislationContext.Provider>
  );
};

Legislation.propTypes = {
  /**
   * External classes
   */
  className: PropTypes.string,
};

export default Legislation;
